import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  SquaresFour, MagnifyingGlass, ClockCounterClockwise, Sparkle, Buildings, GearSix,
  SignOut, Sun, Moon, Monitor,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useI18n } from "@/context/I18nContext";
import { Wordmark } from "@/components/Brand";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";

const navItems = [
  { to: "/panel", label: "Kontrol Paneli", labelEn: "Dashboard", icon: SquaresFour, testid: "nav-dashboard" },
  { to: "/arama", label: "Arama", labelEn: "Search", icon: MagnifyingGlass, testid: "nav-search" },
  { to: "/gecmis", label: "Geçmiş", labelEn: "History", icon: ClockCounterClockwise, testid: "nav-history" },
  { to: "/ai-analiz", label: "AI Öngörüleri", labelEn: "AI Insights", icon: Sparkle, testid: "nav-ai" },
  { to: "/isletme", label: "İşletme Profili", labelEn: "Business Profile", icon: Buildings, testid: "nav-business" },
  { to: "/ayarlar", label: "Ayarlar", labelEn: "Settings", icon: GearSix, testid: "nav-settings" },
];

export default function Sidebar({ onNavigate }) {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const { locale } = useI18n();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/giris");
  };

  return (
    <aside className="h-full flex flex-col bg-card border-r border-border/60 w-64">
      {/* Wordmark — brand IS the logo (no icon/monogram) */}
      <div className="px-6 py-6 border-b border-border/60">
        <Wordmark as={NavLink} to="/panel" onClick={onNavigate} size="sm" showTagline taglineOverride={locale === "en" ? "AI Business Development Employee" : "Yapay Zekâ İş Geliştirme Çalışanı"} data-testid="sidebar-brand" />
      </div>

      {/* Nav */}
      <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={onNavigate}
            data-testid={item.testid}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium ${
                isActive
                  ? "bg-primary/10 text-primary border border-primary/20"
                  : "text-muted-foreground hover:text-foreground hover:bg-secondary border border-transparent"
              }`
            }
          >
            <item.icon size={18} weight="duotone" />
            <span>{locale === "en" ? item.labelEn : item.label}</span>
          </NavLink>
        ))}
      </nav>

      {/* Bottom */}
      <div className="border-t border-border/60 p-3 space-y-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="w-full justify-start gap-2" data-testid="theme-toggle">
              {theme === "light" && <Sun size={16} />}
              {theme === "dark" && <Moon size={16} />}
              {theme === "system" && <Monitor size={16} />}
              <span className="text-xs">Tema: {theme === "light" ? "Aydınlık" : theme === "dark" ? "Karanlık" : "Sistem"}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuItem onClick={() => setTheme("light")} data-testid="theme-light">
              <Sun size={14} className="mr-2" /> Aydınlık
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setTheme("dark")} data-testid="theme-dark">
              <Moon size={14} className="mr-2" /> Karanlık
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setTheme("system")} data-testid="theme-system">
              <Monitor size={14} className="mr-2" /> Sistem
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <div className="flex items-center gap-2 px-2 py-2 rounded-md bg-secondary/60">
          <div className="w-7 h-7 rounded-full bg-primary/20 text-primary grid place-items-center text-xs font-bold">
            {(user?.name || "U").charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold truncate">{user?.name || "Kullanıcı"}</div>
            <div className="text-[10px] text-muted-foreground truncate">{user?.email}</div>
          </div>
          <button onClick={handleLogout} data-testid="logout-btn" className="text-muted-foreground hover:text-destructive">
            <SignOut size={16} />
          </button>
        </div>
      </div>
    </aside>
  );
}
